/**
 * Typed nlocal API client.
 *
 * Thin wrapper over `openapi-fetch` bound to the generated `paths` type, so every call site
 * is checked against the control plane's OpenAPI contract (§5). Adds the two things every
 * nlocal request needs and the spec does not express: Sanctum's same-site cookie credentials
 * and the `X-Workspace-Id` scoping header (§5, §12.3).
 */
import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'

export type { paths, components, operations } from './schema'

/** §5 error envelope. Every non-2xx response from the API has this shape. */
export interface ApiError {
  error: {
    code: string
    message: string
    docs?: string
    details?: Record<string, unknown>
  }
}

export interface ClientOptions {
  /** e.g. `https://api.nlocal.dev/v1` */
  baseUrl: string
  /** Resolves the active workspace id for the `X-Workspace-Id` header. */
  workspaceId?: () => string | null | undefined
  /** Bearer token for CLI/API-key callers. Omit in the browser — Sanctum uses cookies. */
  token?: () => string | null | undefined
}

export function createApiClient({ baseUrl, workspaceId, token }: ClientOptions) {
  const client = createClient<paths>({
    baseUrl,
    // Sanctum SPA auth is cookie based (§6.1). 'same-origin' rather than 'include' because
    // Laravel serves the dashboard from the API's own origin (DECISIONS.md D-009); callers
    // that do run cross-origin pass their own fetch options.
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })

  const scope: Middleware = {
    async onRequest({ request }) {
      const ws = workspaceId?.()
      if (ws) request.headers.set('X-Workspace-Id', ws)

      const bearer = token?.()
      if (bearer) request.headers.set('Authorization', `Bearer ${bearer}`)

      return request
    },
  }

  client.use(scope)
  return client
}

export type ApiClient = ReturnType<typeof createApiClient>

/** Narrow an unknown rejection to the §5 error envelope. */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiError).error?.code === 'string'
  )
}
